import Link from "next/link";
import type { SpotVerdictLine as SpotVerdictLineModel } from "@/lib/areas/spot-verdict";

const ACTION_STYLE: Readonly<Record<SpotVerdictLineModel["action"], string>> = {
  "go-now": "border-emerald-200 bg-emerald-50 text-emerald-900",
  "go-later": "border-sky-200 bg-sky-50 text-sky-900",
  "go-elsewhere": "border-amber-200 bg-amber-50 text-amber-900",
  "do-not-bother": "border-border-light bg-white text-navy",
};

/**
 * The one-line spot verdict under the verdict block (spec 5.15 and 10.4 item 5). Presentational
 * only: the line is built by buildSpotVerdictLine from the spot's own area row. Live numbers sit
 * in a data-nosnippet block so search snippets never quote a stale score.
 */
export function SpotVerdictLine({
  line,
  windowHref,
  safetyHref = null,
}: {
  line: SpotVerdictLineModel;
  windowHref: string | null;
  /** The page's safety section, linked when this spot is red-flagged ("Check safety first"). */
  safetyHref?: string | null;
}) {
  const { elsewhere } = line;
  return (
    <div
      data-nosnippet=""
      data-spot-verdict={line.action}
      className={`mt-4 rounded-[14px] border px-4 py-3 text-[14px] leading-snug ${ACTION_STYLE[line.action]}`}
    >
      <p className="font-semibold">
        {elsewhere ? (
          <>
            {line.text}{" "}
            <Link href={elsewhere.href} className="inline-flex min-h-11 items-center underline underline-offset-2">
              Open {elsewhere.displayName}
            </Link>
          </>
        ) : line.text}
      </p>
      {line.abilityNote ? <p className="mt-1 text-[13px] font-medium">{line.abilityNote}</p> : null}
      {safetyHref ? (
        <a href={safetyHref} className="mt-1 inline-flex min-h-11 items-center text-[13px] font-semibold underline underline-offset-2">
          Read the safety notes
        </a>
      ) : null}
      {windowHref && line.action !== "go-elsewhere" && line.action !== "do-not-bother" ? (
        <Link href={windowHref} className="mt-1 inline-flex min-h-11 items-center text-[13px] font-semibold underline underline-offset-2">
          See the best window here
        </Link>
      ) : null}
    </div>
  );
}
